import type { ReactNode } from "react"
import { formatDateString } from "@/v2-common/utils/utils.date"
import {
  LICENSE_SOURCE_FENERUM,
  LICENSE_SOURCE_OPERATOR,
  LICENSE_STATUS_EXPIRED,
  LICENSE_STATUS_TRIAL,
} from "@/v2-backoffice-client/license/license.constants"
import {
  getDaysBetween,
  getTrialFacts,
} from "@/v2-backoffice-client/license/license.utils"

/**
 * Every lifecycle fact the tooltip can render — all optional, because callers
 * range from the full license row (licenses board) to the customers board's
 * governing aggregate to the user rows' thin status+trial pair.
 */
export type LicenseBadgeTooltipLicense = {
  LicenseStatusCode: number
  TrialEndsAt?: string | null
  StartedAt?: string | null
  RenewsAt?: string | null
  /** End date: the stamp on Expired rows, the scheduled end on Not renewing. */
  CancelledAt?: string | null
  PlanName?: string | null
  PlanDeprecatedAt?: string | null
  Seats?: number | null
  LicenseSourceCode?: number | null
  OwnerName?: string | null
}

/** Whether the license shape carries anything beyond status + trial clock. */
export function hasLicenseTooltipFacts(
  license: LicenseBadgeTooltipLicense,
): boolean {
  return Boolean(
    license.StartedAt ??
    license.RenewsAt ??
    license.CancelledAt ??
    license.PlanName ??
    license.Seats ??
    license.LicenseSourceCode ??
    license.OwnerName,
  )
}

export type LicenseBadgeTooltipProps = {
  license: LicenseBadgeTooltipLicense
  statusName?: string
}

function Row(props: { label: string; children: ReactNode }) {
  const { label, children } = props
  return (
    <>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-right">{children}</dd>
    </>
  )
}

// The license lifecycle at a hover: what they hold, since when, where the
// clock stands (trial length and countdown, renewal, end date), where the
// row comes from, and who owns the CRM conversation — so the badge answers
// the follow-up questions without a trip into the panel. Rows render only
// when the caller's read carries the fact.
function LicenseBadgeTooltip(props: LicenseBadgeTooltipProps) {
  const { license, statusName } = props

  const isExpired = license.LicenseStatusCode === LICENSE_STATUS_EXPIRED
  const isTrial = license.LicenseStatusCode === LICENSE_STATUS_TRIAL
  const trialFacts = getTrialFacts(license)
  const trialDays =
    license.StartedAt && license.TrialEndsAt
      ? getDaysBetween(license.StartedAt, license.TrialEndsAt)
      : null

  // The customers board hands over every held license as one joined
  // string; each gets its own line, since a list wrapped mid-name and
  // right-aligned read as one long title.
  const planNames = (license.PlanName ?? "")
    .split(", ")
    .map((name) => name.trim())
    .filter(Boolean)

  return (
    <div className="flex min-w-44 max-w-72 flex-col gap-1.5">
      {statusName && <p className="font-medium">{statusName}</p>}

      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-0.5">
        {planNames.length > 0 && (
          <Row label={planNames.length > 1 ? "Licenses" : "License"}>
            <span className="flex flex-col items-end gap-0.5">
              {planNames.map((name) => (
                <span key={name} className="whitespace-nowrap">
                  {name}
                </span>
              ))}
              {license.PlanDeprecatedAt && (
                <span className="text-muted-foreground">deprecated</span>
              )}
            </span>
          </Row>
        )}

        {license.Seats != null && <Row label="Seats">{license.Seats}</Row>}

        {license.StartedAt && (
          <Row label="Started">{formatDateString(license.StartedAt)}</Row>
        )}

        {license.TrialEndsAt && (
          <Row label={isTrial ? "Trial ends" : "Trial ended"}>
            {formatDateString(license.TrialEndsAt)}
            {trialDays != null && trialDays > 0 ? ` (${trialDays}-day)` : ""}
          </Row>
        )}

        {!isExpired && license.RenewsAt && (
          <Row label="Renews">{formatDateString(license.RenewsAt)}</Row>
        )}

        {license.CancelledAt && (
          <Row label={isExpired ? "Ended" : "Ends"}>
            {formatDateString(license.CancelledAt)}
          </Row>
        )}

        {license.LicenseSourceCode === LICENSE_SOURCE_FENERUM && (
          <Row label="Source">Fenerum subscription</Row>
        )}
        {license.LicenseSourceCode === LICENSE_SOURCE_OPERATOR && (
          <Row label="Source">Operator grant</Row>
        )}

        {license.OwnerName && <Row label="CRM owner">{license.OwnerName}</Row>}
      </dl>

      {trialFacts?.isOverdue && (
        <p className="text-muted-foreground">
          The trial ran out {trialFacts.label.replace(" overdue", "")} ago —
          convert it or end it.
        </p>
      )}
    </div>
  )
}

export default LicenseBadgeTooltip
